import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Input, Modal, Spin } from "antd";
import { LikeFilled, LikeOutlined, StarFilled, StarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { EasyAvatar } from "fin-web-components";
import type { IntelCommentKind } from "../../platform_spaces/space/SpaceType";
import { IntelligenceFeedApi } from "./IntelligenceFeedApi";
import type { IntelligenceFeedItem, IntelligenceFeedListKind } from "./IntelligenceFeedType";
import { useStockLinkDetail } from "../../components/fin-web/useStockLinkDetail";
import { IntelStockPickCard } from "./IntelStockPickCard";
import { intelPageCssVars, INTEL_IMPORTANT_MIN, INTEL_NEWS_TYPE_OPTIONS, intelImportanceLabel } from "./Config";
import "./IntelligenceFeedPage.css";
import { avatarDisplayWord } from "../../utils/avatarDisplayWord";
import { formatEngagementCount } from "../../utils/formatEngagementCount";

const PAGE_SIZE = 8;

const DETAIL_INTRO_AI =
  "风险提示和关联个股由AI根据规则生成，仅供参考，不构成投资建议。请理性分析和参考。";

function IntelFeedEngagementActions({
  row,
  onToggleLike,
  onToggleCollect,
}: {
  row: IntelligenceFeedItem;
  onToggleLike: (e: React.MouseEvent, row: IntelligenceFeedItem) => void;
  onToggleCollect: (e: React.MouseEvent, row: IntelligenceFeedItem) => void;
}) {
  const liked = Boolean(row.likedByMe);
  const collected = Boolean(row.collectedByMe);
  return (
    <div className="lf-intel-card-actions" aria-label="点赞与收藏">
      <button
        type="button"
        className="lf-intel-action-btn"
        onClick={(e) => void onToggleLike(e, row)}
        aria-label={liked ? "取消点赞" : "点赞"}
      >
        {liked ? (
          <LikeFilled className="lf-intel-action-ic lf-intel-action-ic--on" />
        ) : (
          <LikeOutlined className="lf-intel-action-ic" />
        )}
        <span className="lf-intel-action-num">{formatEngagementCount(row.likeCount)}</span>
      </button>
      <button
        type="button"
        className="lf-intel-action-btn"
        onClick={(e) => void onToggleCollect(e, row)}
        aria-label={collected ? "取消收藏" : "收藏"}
      >
        {collected ? (
          <StarFilled className="lf-intel-action-ic lf-intel-action-ic--collect-on" />
        ) : (
          <StarOutlined className="lf-intel-action-ic" />
        )}
        <span className="lf-intel-action-num">{formatEngagementCount(row.collectCount)}</span>
      </button>
    </div>
  );
}

function IntelImportanceStrength({ value }: { value: number }) {
  const filled = Math.max(1, Math.min(5, Math.ceil(value / 2)));
  const important = value >= INTEL_IMPORTANT_MIN;
  return (
    <span
      className={`lf-intel-strength${important ? " lf-intel-strength--important" : ""}`}
      aria-label={`情报强度 ${value} 分`}
    >
      <span className="lf-intel-strength__label">强度</span>
      <span className="lf-intel-strength__bars" aria-hidden>
        {Array.from({ length: 5 }, (_, index) => (
          <i key={index} className={index < filled ? "is-filled" : ""} />
        ))}
      </span>
      <span className="lf-intel-strength__badge">{intelImportanceLabel(value)}</span>
    </span>
  );
}

function listSecondaryHint(row: IntelligenceFeedItem, mode: IntelCommentKind): string {
  if (mode !== "ai_recommend") {
    return "";
  }
  if (row.extraKind === "ai_risk") {
    return "（1个风险提示）";
  }
  const n = row.stocks?.length ?? 0;
  return `（${n}个关联标的）`;
}

function IntelFeedSummary({
  row,
  mode,
  className,
}: {
  row: IntelligenceFeedItem;
  mode: IntelCommentKind;
  className?: string;
}) {
  const hint = listSecondaryHint(row, mode);
  const manualGrey = mode === "manual_comment" && !row.hasTeacherComment;
  const secondaryLine =
    mode === "manual_comment" ? (row.hasTeacherComment ? "老师已点评" : "尚无老师点评") : hint;
  return (
    <div className={className ?? "lf-intel-summary"} style={{ fontWeight: 300 }}>
      {row.summary}
      {secondaryLine ? (
        <span
          className={["lf-intel-rec-hint-inline", manualGrey ? "lf-intel-rec-hint--muted" : ""]
            .filter(Boolean)
            .join(" ")}
          style={{ fontWeight: 300 }}
        >
          {secondaryLine}
        </span>
      ) : null}
    </div>
  );
}

function openCardKey(e: React.KeyboardEvent, open: () => void) {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    open();
  }
}

/** 接口日期经 `backend.post` → `convertDates` 后为 Date；展示：YYYY年MM月DD日 HH:mm */
function apiDateTimeToChineseMinute(v: unknown): string {
  if (v == null || v === "") {
    return "";
  }
  if (v instanceof Date) {
    return dayjs(v).format("YYYY年MM月DD日 HH:mm");
  }
  if (typeof v === "string") {
    const t = v.trim();
    const d = dayjs(t);
    return d.isValid() ? d.format("YYYY年MM月DD日 HH:mm") : t;
  }
  return String(v).trim();
}

function newsTypeLabel(type?: number): string {
  return INTEL_NEWS_TYPE_OPTIONS.find((option) => option.type === type)?.label ?? "综合新闻";
}

/** 静默轮询：用首页覆盖并前置新条，保留已加载分页且去重 */
function mergePollFirstPage(
  prev: IntelligenceFeedItem[],
  firstPage: IntelligenceFeedItem[]
): IntelligenceFeedItem[] {
  const firstIds = new Set(firstPage.map((item) => item.id));
  const rest = prev.filter((item) => !firstIds.has(item.id));
  return [...firstPage, ...rest];
}

export interface IntelligenceFeedPageProps {
  spaceId: string;
  spaceProductId: string;
  spaceDisplayName: string;
  intelCommentKind: IntelCommentKind;
}

const IntelligenceFeedPage: React.FC<IntelligenceFeedPageProps> = ({
  spaceId,
  spaceProductId,
  spaceDisplayName,
  intelCommentKind,
}) => {
  const isNoneMode = intelCommentKind === "none";
  const [draftKeyword, setDraftKeyword] = useState("");
  const [applied, setApplied] = useState({
    keyword: "" as string | undefined,
  });
  const [feedScope, setFeedScope] = useState<IntelligenceFeedListKind>("all");
  const [selectedNewsType, setSelectedNewsType] = useState<number | null>(null);
  const [importantOnly, setImportantOnly] = useState(false);
  const [items, setItems] = useState<IntelligenceFeedItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedRow, setSelectedRow] = useState<IntelligenceFeedItem | null>(null);
  const [detailModalRow, setDetailModalRow] = useState<IntelligenceFeedItem | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const feedScrollRef = useRef<HTMLDivElement | null>(null);
  const scrollAfterNextListFetchRef = useRef(false);
  const { stockLinkDetail, requestStockDetailForLink, buildStockNotesTabProps } =
    useStockLinkDetail(spaceId);

  const hasActiveFilter = useMemo(
    () => !!applied.keyword?.trim() || selectedNewsType != null || importantOnly,
    [applied, selectedNewsType, importantOnly]
  );

  const fetchFirstPage = useCallback(
    async (opts?: { scope?: IntelligenceFeedListKind; silent?: boolean }) => {
      const scope = opts?.scope ?? feedScope;
      const silent = Boolean(opts?.silent);
      if (!silent) {
        setLoading(true);
      }
      try {
        const res = await IntelligenceFeedApi.list(scope, {
          spaceId,
          spaceProductId,
          pageNum: 1,
          pageSize: PAGE_SIZE,
          keyword: applied.keyword?.trim() || undefined,
          type: selectedNewsType ?? undefined,
          minImportance: importantOnly ? INTEL_IMPORTANT_MIN : undefined,
        });
        if (typeof res === "string") {
          if (!silent) {
            setItems([]);
            setTotal(0);
          }
          return;
        }
        if (silent) {
          const latestById = new Map(res.list.map((item) => [item.id, item]));
          setItems((prev) => mergePollFirstPage(prev, res.list));
          setSelectedRow((sr) => (sr && latestById.has(sr.id) ? latestById.get(sr.id)! : sr));
          setDetailModalRow((dr) =>
            dr && latestById.has(dr.id) ? latestById.get(dr.id)! : dr
          );
        } else {
          setItems(res.list);
        }
        setTotal(res.total);
      } finally {
        if (!silent) {
          setLoading(false);
        }
        if (scrollAfterNextListFetchRef.current && !silent) {
          scrollAfterNextListFetchRef.current = false;
          requestAnimationFrame(() => {
            feedScrollRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
          });
        }
      }
    },
    [spaceId, spaceProductId, applied, feedScope, selectedNewsType, importantOnly]
  );

  const loadMore = useCallback(async () => {
    if (loadingMore || total === 0 || items.length >= total) {
      return;
    }
    const nextPage = Math.floor(items.length / PAGE_SIZE) + 1;
    setLoadingMore(true);
    try {
      const res = await IntelligenceFeedApi.list(feedScope, {
        spaceId,
        spaceProductId,
        pageNum: nextPage,
        pageSize: PAGE_SIZE,
        keyword: applied.keyword?.trim() || undefined,
        type: selectedNewsType ?? undefined,
        minImportance: importantOnly ? INTEL_IMPORTANT_MIN : undefined,
      });
      if (typeof res === "string") {
        return;
      }
      setTotal(res.total);
      setItems((prev) => [...prev, ...res.list]);
    } finally {
      setLoadingMore(false);
    }
  }, [
    loadingMore,
    total,
    items.length,
    spaceId,
    spaceProductId,
    applied,
    feedScope,
    selectedNewsType,
    importantOnly,
  ]);

  useEffect(() => {
    void fetchFirstPage();
  }, [fetchFirstPage]);

  useEffect(() => {
    const id = window.setInterval(() => {
      void fetchFirstPage({ silent: true });
    }, 10_000);
    return () => window.clearInterval(id);
  }, [fetchFirstPage]);

  useEffect(() => {
    if (intelCommentKind !== "none") {
      return;
    }
    setSelectedRow(null);
  }, [intelCommentKind]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) {
      return undefined;
    }
    const ob = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting || loading || loadingMore) {
          return;
        }
        void loadMore();
      },
      { root: null, rootMargin: "120px", threshold: 0 }
    );
    ob.observe(el);
    return () => ob.disconnect();
  }, [items.length, loading, loadingMore, loadMore]);

  const runQuery = () => {
    setApplied({
      keyword: draftKeyword.trim() || undefined,
    });
  };

  const clearFilters = () => {
    setDraftKeyword("");
    setApplied({ keyword: undefined });
    setSelectedNewsType(null);
    setImportantOnly(false);
  };

  const onViewLatest = useCallback(() => {
    scrollAfterNextListFetchRef.current = true;
    setFeedScope("all");
    setSelectedNewsType(null);
    setImportantOnly(false);
    if (feedScope === "all" && selectedNewsType == null && !importantOnly) {
      void fetchFirstPage({ scope: "all" });
    }
  }, [feedScope, selectedNewsType, importantOnly, fetchFirstPage]);

  const selectRow = useCallback((row: IntelligenceFeedItem) => {
    setSelectedRow(row);
  }, []);

  const patchEngagement = useCallback(
    (id: string, liked: boolean, collected: boolean) => {
      const applyPatch = (r: IntelligenceFeedItem): IntelligenceFeedItem => {
        if (r.id !== id) return r;
        let likeCount = r.likeCount ?? 0;
        let collectCount = r.collectCount ?? 0;
        const wasLiked = Boolean(r.likedByMe);
        const wasColl = Boolean(r.collectedByMe);
        if (liked !== wasLiked) {
          likeCount += liked ? 1 : -1;
        }
        if (collected !== wasColl) {
          collectCount += collected ? 1 : -1;
        }
        return {
          ...r,
          likedByMe: liked,
          collectedByMe: collected,
          likeCount: Math.max(0, likeCount),
          collectCount: Math.max(0, collectCount),
        };
      };
      setItems((prev) => prev.map(applyPatch));
      setSelectedRow((sr) => (sr ? applyPatch(sr) : sr));
      setDetailModalRow((dr) => (dr ? applyPatch(dr) : dr));
    },
    []
  );

  const removeFromList = useCallback((id: string) => {
    setItems((prev) => prev.filter((r) => r.id !== id));
    setSelectedRow((sr) => (sr?.id === id ? null : sr));
    setTotal((t) => Math.max(0, t - 1));
  }, []);

  const onToggleLike = useCallback(
    async (e: React.MouseEvent, row: IntelligenceFeedItem) => {
      e.stopPropagation();
      const res = await IntelligenceFeedApi.toggleLike({
        spaceId,
        spaceProductId,
        intelligenceId: row.id,
      });
      if (typeof res === "string") {
        return;
      }
      if (feedScope === "liked" && !res.liked) {
        removeFromList(row.id);
        return;
      }
      patchEngagement(row.id, res.liked, res.collected);
    },
    [spaceId, spaceProductId, feedScope, patchEngagement, removeFromList]
  );

  const onToggleCollect = useCallback(
    async (e: React.MouseEvent, row: IntelligenceFeedItem) => {
      e.stopPropagation();
      const res = await IntelligenceFeedApi.toggleCollect({
        spaceId,
        spaceProductId,
        intelligenceId: row.id,
      });
      if (typeof res === "string") {
        return;
      }
      if (feedScope === "collected" && !res.collected) {
        removeFromList(row.id);
        return;
      }
      patchEngagement(row.id, res.liked, res.collected);
    },
    [spaceId, spaceProductId, feedScope, patchEngagement, removeFromList]
  );

  const showEnd = !loading && items.length > 0 && items.length >= total;

  const renderDetailBody = (row: IntelligenceFeedItem): React.ReactNode => {
    if (intelCommentKind === "manual_comment") {
      const t = row.teacher;
      const has = row.hasTeacherComment;
      if (!has) {
        return (
          <div className="lf-intel-detail-body">
            <p className="lf-intel-muted">本条尚无老师点评，请稍后再来查看。</p>
          </div>
        );
      }
      return (
        <div className="lf-intel-detail-body">
          <EasyAvatar
            shape="square"
            squareRadius={0}
            size={48}
            url={t?.avatarUrl?.trim() || undefined}
            word={avatarDisplayWord((t?.nickname || t?.realName || "老师").trim() || "老师")}
            alt=""
            className="lf-intel-teacher-avatar"
          />
          <div className="lf-intel-teacher-name">{(t?.nickname || t?.realName || "老师").trim() || "老师"}</div>
          <div className="lf-intel-teacher-body" style={{ whiteSpace: "pre-wrap" }}>
            {row.teacherComment}
          </div>
        </div>
      );
    }

    return (
      <div className="lf-intel-detail-body">
        <p className="lf-intel-detail-disclaimer">{DETAIL_INTRO_AI}</p>
        {row.extraKind === "ai_risk" ? (
          <>
            <div className="lf-intel-detail-section-title">风险提示</div>
            <div className="lf-intel-detail-risk-content">{row.riskDetail || "—"}</div>
          </>
        ) : (
          <>
            <div className="lf-intel-detail-section-title">关联个股</div>
            <div className="lf-intel-stocks">
              {row.stocks.map((s) => (
                <IntelStockPickCard
                  key={`${row.id}-${s.code}`}
                  pick={s}
                  stockLinkDetail={stockLinkDetail}
                  requestStockDetailForLink={requestStockDetailForLink}
                  stockNotesTab={buildStockNotesTabProps(s.code.trim(), s.name)}
                />
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderRight = () => {
    if (!selectedRow) {
      return (
        <aside className="lf-intel-detail" aria-label="详情">
          <div className="lf-intel-detail-empty">点击左侧情报查看详情</div>
        </aside>
      );
    }

    const asideLabel = intelCommentKind === "manual_comment" ? "老师点评" : "关联说明";

    return (
      <aside className="lf-intel-detail" aria-label={asideLabel}>
        {renderDetailBody(selectedRow)}
      </aside>
    );
  };

  return (
    <div className="lf-intel-page" style={intelPageCssVars()}>
      <div className={`lf-intel-centered${isNoneMode ? " lf-intel-centered--solo" : ""}`}>
        <div className="lf-intel-sticky-header">
          <div className={`lf-intel-filter${isNoneMode ? " lf-intel-filter--wide" : ""}`}>
            <div className="lf-intel-filter-grow">
              <Input
                allowClear
                placeholder="关键词"
                className="lf-intel-square lf-intel-kw lf-intel-kw--grow"
                value={draftKeyword}
                onChange={(e) => setDraftKeyword(e.target.value)}
                onPressEnter={runQuery}
              />
            </div>
            <div className="lf-intel-filter-actions">
              {hasActiveFilter ? (
                <Button type="link" className="lf-intel-link" onClick={clearFilters}>
                  清空条件
                </Button>
              ) : null}
              <Button type="primary" className="lf-intel-btn lf-intel-search-btn" style={{ minWidth: 120 }} onClick={runQuery}>
                搜索
              </Button>
              <Button className="lf-intel-btn lf-intel-view-latest-btn" style={{ minWidth: 120 }} onClick={onViewLatest}>
                查看最新
              </Button>
            </div>
          </div>

          <div
            className={`lf-intel-scope-row${isNoneMode ? " lf-intel-scope-row--wide" : ""}`}
          >
            <div
              className="lf-intel-scope-filters"
              role="radiogroup"
              aria-label="情报范围"
              style={{marginLeft: '3px'}}
            >
              <button
                type="button"
                role="radio"
                aria-checked={feedScope === "all"}
                className={`lf-intel-scope-filter${feedScope === "all" ? " lf-intel-scope-filter--active" : ""}`}
                onClick={() => setFeedScope("all")}
              >
                全部
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={feedScope === "liked"}
                className={`lf-intel-scope-filter${feedScope === "liked" ? " lf-intel-scope-filter--active" : ""}`}
                onClick={() => setFeedScope("liked")}
              >
                点赞过的
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={feedScope === "collected"}
                className={`lf-intel-scope-filter${feedScope === "collected" ? " lf-intel-scope-filter--active" : ""}`}
                onClick={() => setFeedScope("collected")}
              >
                我的收藏
              </button>
              <button
                type="button"
                className={`lf-intel-scope-filter${
                  importantOnly ? " lf-intel-scope-filter--active" : ""
                }`}
                onClick={() => setImportantOnly((prev) => !prev)}
              >
                重要
              </button>
              {INTEL_NEWS_TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.type}
                  type="button"
                  className={`lf-intel-scope-filter lf-intel-type-filter${
                    selectedNewsType === opt.type ? " lf-intel-scope-filter--active" : ""
                  }`}
                  onClick={() =>
                    setSelectedNewsType((prev) => (prev === opt.type ? null : opt.type))
                  }
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div
            className="lf-intel-live-banner-row"
            aria-live="polite"
          >
            <span className="lf-intel-banner lf-intel-banner--live" style={{ fontSize: "14px" }}>
              7×24 小时情报更新中…
            </span>
          </div>
        </div>

        <div className={isNoneMode ? "lf-intel-main lf-intel-main--solo" : "lf-intel-main lf-intel-main--split"}>
          <div className={`lf-intel-col${isNoneMode ? " lf-intel-col--solo" : ""}`}>
            <div className="lf-intel-feed-scroll" ref={feedScrollRef}>
              <Spin spinning={loading}>
                <div className="lf-intel-waterfall">
                  {items.map((row) => {
                    const cardSelectable = !isNoneMode;
                    return (
                      <article
                        key={row.id}
                        className={[
                          "lf-intel-card",
                          cardSelectable ? "lf-intel-card--clickable" : "",
                          cardSelectable && selectedRow?.id === row.id ? "lf-intel-card--active" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        {...(cardSelectable
                          ? ({
                              role: "button",
                              tabIndex: 0,
                              onClick: () => selectRow(row),
                              onKeyDown: (e: React.KeyboardEvent) =>
                                openCardKey(e, () => selectRow(row)),
                            } as const)
                          : {})}
                      >
                        <div className="lf-intel-card-head">
                          <div className="lf-intel-card-title-stack">
                            <h2
                              className="lf-intel-title"
                              style={{ fontWeight: 400, cursor: 'pointer' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setDetailModalRow(row);
                              }}
                            >
                              {row.title}
                            </h2>
                            <div className="lf-intel-datetime-row">
                              <span className="lf-intel-datetime">
                                {apiDateTimeToChineseMinute(row.occurredAt)}
                              </span>
                              <span className="lf-intel-type-text">{newsTypeLabel(row.type)}</span>
                            </div>
                          </div>
                          <IntelFeedEngagementActions
                            row={row}
                            onToggleLike={onToggleLike}
                            onToggleCollect={onToggleCollect}
                          />
                        </div>
                        <IntelFeedSummary row={row} mode={intelCommentKind} />
                        <div className="lf-intel-meta-row">
                          <IntelImportanceStrength value={row.importance} />
                        </div>
                      </article>
                    );
                  })}
                  <div ref={sentinelRef} className="lf-intel-sentinel" />
                  {loadingMore ? (
                    <div className="lf-intel-more">
                      <Spin size="small" /> 加载中…
                    </div>
                  ) : null}
                  {showEnd ? <div className="lf-intel-end">已经到底啦</div> : null}
                </div>
              </Spin>
            </div>
          </div>
          {!isNoneMode ? renderRight() : null}
        </div>
      </div>

      <Modal
        className="lf-intel-detail-modal"
        title={
          detailModalRow ? (
            <div className="lf-intel-modal-title-stack">
              <div className="lf-intel-modal-title-row">
                <div className="lf-intel-modal-title-line">{detailModalRow.title}</div>
                <IntelFeedEngagementActions
                  row={detailModalRow}
                  onToggleLike={onToggleLike}
                  onToggleCollect={onToggleCollect}
                />
              </div>
              <div className="lf-intel-modal-datetime-stars-row">
                <span className="lf-intel-modal-datetime-under-title">
                  {apiDateTimeToChineseMinute(detailModalRow.occurredAt)}
                </span>
                <IntelImportanceStrength value={detailModalRow.importance} />
              </div>
            </div>
          ) : (
            ""
          )
        }
        open={detailModalRow != null}
        onCancel={() => setDetailModalRow(null)}
        footer={null}
        width={720}
        centered
        destroyOnClose
      >
        {detailModalRow ? (
          <div className="lf-intel-modal-inner">
            <div className="lf-intel-modal-scroll">
              <IntelFeedSummary
                row={detailModalRow}
                mode={intelCommentKind}
                className="lf-intel-summary lf-intel-modal-summary"
              />
              {intelCommentKind === "manual_comment" ? (
                <div className="lf-intel-modal-detail-block">
                  {renderDetailBody(detailModalRow)}
                </div>
              ) : null}
            </div>
            <div className="lf-intel-modal-brand-row">
              <span className="lf-intel-modal-brand-line">
                龙粉社ID：
                <span className="lf-intel-modal-brand-id">{spaceId}</span>
                <span className="lf-intel-modal-brand-parens" style={{marginLeft: '4px'}}>
                   - <span className="lf-intel-modal-brand-name">{spaceDisplayName}</span> 
                </span>
              </span>
              <a
                className="lf-intel-modal-brand-url"
                href="https://www.longfans.com"
                target="_blank"
                rel="noopener noreferrer" 
              >
                www.longfans.com
              </a>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default IntelligenceFeedPage;
